import React, { useState, useEffect, useRef, useMemo, lazy, Suspense } from "react";
import MemoizedGlobeComponent, { BACKEND_URL } from "./GlobeComponent";
import { trackEvent, trackVisit } from "./analytics";
import "./App.css";
import TwitterIcon from '@mui/icons-material/Twitter';
import Fab from '@mui/material/Fab';
import Slider from '@mui/material/Slider';
import { SCALES, buildScale, formatTimeParam, parseTimeParams, parseScaleParam } from "./timeline-time";
import MissionTimeline from "./MissionTimeline";
import TimelineHistogram, { histogramDays } from "./TimelineHistogram";
import { ARTEMIS_II_URL, MISSION_INTRO_MS, createMission } from "./artemis-mission";

// Desktop only (phones read stories in the hex callout), so phones never
// download it or the Joy UI it's built from.
const Popup = lazy(() => import("./Popup"));

const INITIAL_PARAMS = new URLSearchParams(window.location.search);
const INITIAL_TIME = parseTimeParams(INITIAL_PARAMS);
const INITIAL_SCALE = parseScaleParam(INITIAL_PARAMS, INITIAL_TIME);
const SCALE_LABELS = { all: "All", month: "Month", day: "Day" };

function App() {
  const hasReportedFirstLoad = useRef(false);
  const [articles, setArticles] = useState([]);
  const [stories, setStories] = useState([]);
  const [dataError, setDataError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [focusedStory, setFocusedStory] = useState(null);
  const [selectedStoryId, setSelectedStoryId] = useState(INITIAL_PARAMS.get("story") || "");
  const [selectedCategory, setSelectedCategory] = useState(INITIAL_PARAMS.get("category") || "");
  const [selectedCountry, setSelectedCountry] = useState(INITIAL_PARAMS.get("country") || "");
  const [isMobile, setIsMobile] = useState(false);
  const [start, setStart] = useState(false);
  // `time` is the end of the 24-hour window shown (null = live). The slider's
  // range and step come from the zoom `scale`, anchored on the selection at
  // the moment the scale was chosen.
  const [time, setTime] = useState(INITIAL_TIME);
  const [scale, setScale] = useState(INITIAL_SCALE);
  const [scaleAnchor, setScaleAnchor] = useState(INITIAL_TIME);
  const timeline = useMemo(() => buildScale(scale, scaleAnchor), [scale, scaleAnchor]);
  const [sliderValue, setSliderValue] = useState(() => timeline.toPos(INITIAL_TIME));
  // Stories per UTC day for the histogram behind the slider; none on failure.
  const [dayCounts, setDayCounts] = useState(null);
  // Stories per UTC hour, by day ("YYYY-MM-DD" → 24 counts), for the Day zoom.
  const [hourCounts, setHourCounts] = useState({});
  const requestedHourDays = useRef(new Set());
  // A mission replay (artemis-mission.js) in place of the news, and the
  // moment it shows, which the globe reads every frame.
  const [mission, setMission] = useState(null);
  const missionClock = useRef(null);
  const keyboardStory = stories.find((story) =>
    (!selectedCategory || story.category === selectedCategory) &&
    (!selectedCountry || story.country === selectedCountry)
  );

  useEffect(() => { trackVisit(); }, []);

  useEffect(() => {
    fetch(`${BACKEND_URL}/timeline_counts`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Counts ${r.status}`))))
      .then((data) => setDayCounts(data.days))
      .catch(() => {});
  }, []);

  useEffect(() => {
    for (const day of histogramDays(timeline)) {
      if (requestedHourDays.current.has(day)) continue;
      requestedHourDays.current.add(day);
      fetch(`${BACKEND_URL}/timeline_hours?day=${day}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Hours ${r.status}`))))
        .then((data) => setHourCounts((prev) => ({ ...prev, [day]: data.hours })))
        .catch(() => requestedHourDays.current.delete(day));
    }
  }, [timeline]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (time !== null) params.set("t", formatTimeParam(time));
    if (scale !== "all") params.set("scale", scale);
    if (selectedCategory) params.set("category", selectedCategory);
    if (selectedCountry) params.set("country", selectedCountry);
    if (selectedStoryId) params.set("story", selectedStoryId);
    const query = params.toString().replace(/%3A/g, ":"); // keep t=…T13:37 readable
    // Debounced: scrubbing the timeline changes the time many times a second,
    // and browsers throttle replaceState (Safari: 100 calls per 10 s).
    const timer = setTimeout(() => {
      window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    }, 300);
    return () => clearTimeout(timer);
  }, [time, scale, selectedCategory, selectedCountry, selectedStoryId]);

  useEffect(() => {
    if (!selectedStoryId) return;
    const story = stories.find((item) => item.article_id === selectedStoryId);
    if (!story) return;
    setFocusedStory({ ...story });
    setArticles([story]);
    setStart(false);
  }, [stories, selectedStoryId]);

  const changeCategory = (value) => { trackEvent("filter_category", value ? "selected" : "cleared"); setSelectedCategory(value); setSelectedStoryId(""); };
  const changeCountry = (value) => { trackEvent("filter_country", value ? "selected" : "cleared"); setSelectedCountry(value); setSelectedStoryId(""); };

  useEffect(() => {
    const media = window.matchMedia?.("(max-width: 767px)");
    if (!media) {
      setIsMobile(window.innerWidth <= 767);
      return;
    }
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  // Update the globe live while dragging, at most once per frame; the globe
  // draws from cached timeline data.
  const pendingSliderValue = useRef(null);
  const showSliderTime = (value) => setTime(timeline.toTime(value));

  const handleSliderChange = (_, value) => {
    setSliderValue(value);
    if (pendingSliderValue.current === null) {
      requestAnimationFrame(() => {
        showSliderTime(pendingSliderValue.current);
        pendingSliderValue.current = null;
      });
    }
    pendingSliderValue.current = value;
  };

  const handleSliderCommit = (_, value) => {
    trackEvent("filter_date", timeline.toTime(value) === null ? "live" : "archive");
    showSliderTime(value);
    setSelectedStoryId("");
  };

  // Day mode slides by the minute; arrow keys still move a whole hour there.
  const handleSliderKeyDown = (event) => {
    if (timeline.step >= timeline.keyStep) return;
    const dir = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[event.key];
    if (!dir) return;
    event.preventDefault();
    const next = Math.min(timeline.max, Math.max(timeline.min, sliderValue + dir * timeline.keyStep));
    handleSliderChange(event, next);
    handleSliderCommit(event, next);
  };

  // Switch zoom level around the current selection, snapping it to the new
  // scale's step (e.g. a mid-day time becomes that whole day in "all").
  const changeScale = (next) => {
    if (next === scale) return;
    trackEvent("timeline_scale", next);
    const model = buildScale(next, time);
    const pos = model.toPos(time);
    setScale(next);
    setScaleAnchor(time);
    setSliderValue(pos);
    setTime(model.toTime(pos));
  };

  const watchMission = (id) => {
    if (id !== "artemis-ii" || mission) return;
    trackEvent("watch_mission", id);
    fetch(ARTEMIS_II_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Mission ${r.status}`))))
      .then((data) => {
        setArticles([]);
        missionClock.current = null;
        setMission(createMission(data));
      })
      .catch(() => setDataError("The mission replay could not be loaded."));
  };
  const exitMission = () => {
    setMission(null);
    missionClock.current = null;
  };

  const fabStyle = {
    position: 'absolute',
    bottom: 16,
    right: 16,
  };

  return (
    <div className="App">
      {keyboardStory && <a className="skip-to-stories" href={keyboardStory.link} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("outbound_article", "keyboard_shortcut")}>Read latest story: {keyboardStory.title}</a>}
      <header className="App-header" style={{ padding: 0 }}>
        <img className="App-logo" src="/logo.png" alt="blue dot news logo" />
      </header>

      {dataError && <div className="globe-data-error ui-panel" role="alert">
        {dataError} <button type="button" onClick={() => { trackEvent("retry_articles"); setRetryKey((value) => value + 1); }}>Try again</button>
      </div>}

      {/* Phones read stories in the hex callout and find About in the menu
          (GlobeComponent). */}
      {!isMobile && (
        <Suspense fallback={null}>
          <Popup articles={articles} isMobile={isMobile} isStart={start} onOutbound={() => trackEvent("outbound_article", "globe")} onWatchMission={watchMission} />
        </Suspense>
      )}

      {!isMobile && (
        <Fab color="primary" aria-label="twitter" sx={fabStyle} href="https://twitter.com/palebluenews">
          <TwitterIcon />
        </Fab>
      )}

      {mission ? (
        <MissionTimeline model={mission} clock={missionClock} startDelayMs={MISSION_INTRO_MS} onExit={exitMission} />
      ) : (
      <div className="timeline-panel ui-panel">
        <div className="timeline-scale" role="group" aria-label="Timeline zoom">
          {SCALES.map((s) => (
            <button
              key={s}
              type="button"
              className={s === scale ? "is-active" : ""}
              aria-pressed={s === scale}
              onClick={() => changeScale(s)}
            >
              {SCALE_LABELS[s]}
            </button>
          ))}
        </div>
        <div className="timeline-slider">
          <TimelineHistogram timeline={timeline} dayCounts={dayCounts} hourCounts={hourCounts} />
          <Slider
            min={timeline.min}
            max={timeline.max}
            step={timeline.step}
            value={sliderValue}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderCommit}
            onKeyDown={handleSliderKeyDown}
            getAriaLabel={() => (scale === "day" ? "News time" : "News day")}
            getAriaValueText={timeline.label}
            marks={timeline.marks}
            valueLabelDisplay="auto"
            valueLabelFormat={timeline.label}
            sx={{
              color: "#fff",
              "& .MuiSlider-thumb": {
                width: 14,
                height: 14,
                boxShadow: "0 0 0 4px rgba(255, 255, 255, 0.25)",
                "&:hover, &.Mui-focusVisible, &.Mui-active": {
                  boxShadow: "0 0 0 7px rgba(255, 255, 255, 0.3)",
                },
              },
              "& .MuiSlider-track": { height: 3, border: 0 },
              "& .MuiSlider-rail": { height: 3, opacity: 0.35 },
              "& .MuiSlider-mark": { backgroundColor: "rgba(255, 255, 255, 0.6)" },
              "& .MuiSlider-markLabel": {
                color: "rgba(255, 255, 255, 0.75)",
                fontFamily: "inherit",
                fontSize: "12px",
              },
              "& .MuiSlider-markLabelActive": { color: "#fff" },
              "& .MuiSlider-valueLabel": {
                background: "#fff",
                color: "var(--ui-accent-ink)",
                borderRadius: "6px",
                fontFamily: "inherit",
                fontSize: "12px",
                fontWeight: 600,
              },
            }}
          />
        </div>
      </div>
      )}

      <MemoizedGlobeComponent
        isMobile={isMobile}
        onFocus={setArticles}
        onOutbound={() => trackEvent("outbound_article", "hex_callout")}
        onExpandStories={() => trackEvent("expand_stories", "hex_callout")}
        onStart={setStart}
        onData={(items) => { trackEvent(hasReportedFirstLoad.current ? "articles_loaded" : "first_load", items.length ? "with_stories" : "empty"); hasReportedFirstLoad.current = true; setStories(items); }}
        onDataError={(error) => { if (error) trackEvent("articles_error"); setDataError(error); }}
        time={time}
        retryKey={retryKey}
        focusedStory={focusedStory}
        selectedCategory={selectedCategory}
        onCategoryChange={changeCategory}
        selectedCountry={selectedCountry}
        onCountryChange={changeCountry}
        mission={mission}
        missionClock={missionClock}
        onWatchMission={watchMission}
      />
    </div>
  );
}

export default App;
